import { Menu, MenuButton, MenuItem } from "@szhsin/react-menu";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdAddCircleOutline, MdChevronRight, MdOutlineDeleteForever, MdSearch } from "react-icons/md";

import classroom_placeholder from "../../../assets/classroom_placeholder.svg";
import { ReactComponent as MenuDots } from "../../../assets/menuDots.svg";
import Button from "../../../components/Button";
import Dropdown from "../../../components/Dropdown";
import LoadingSpinner from "../../../components/LoadingSpinner";
import AddClassroomModal from "../../../components/modals/common/AddClassroomModal";
import DeleteClassroomModal from "../../../components/modals/common/DeleteClassroomModal";
import Pagination from "../../../components/Pagination";
import TruncatedTextWithTooltip from "../../../components/TruncatedTextWithTooltip";
import { USER_TYPE } from "../../../constants/common";
import useAuth from "../../../hooks/useAuth";
import teacherServices from "../../../services/teacherServices";
import { getLanguage } from "../../../utils/getLanguage";
import { useNavigateWithLang } from "../../../utils/navigation";

const TeacherClassrooms = () => {
  const navigate = useNavigateWithLang();
  const lang = getLanguage();
  const { t } = useTranslation();
  const user = useAuth();

  const addClassroomModalRef = useRef();
  const deleteClassroomModalRef = useRef();

  const [loading, setLoading] = useState(true);
  const [teacherClassrooms, setTeacherClassrooms] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const navigateClassroomDetails = (id) => navigate(`/${USER_TYPE.TEACHER.ROLE}/my-classrooms/${id}`);

  const openAddClassroomModal = (teacherID) => addClassroomModalRef.current?.openModal(teacherID);
  const openDeleteClassroomModal = (classroom) => deleteClassroomModalRef.current?.openModal(classroom);

  const handleAddClassroomSuccess = () => {
    getTeacherClassrooms();
  };

  const handleDeleteClassroomSuccess = () => {
    getTeacherClassrooms();
  };
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getTeacherClassrooms = async (page = 1, search = debouncedSearchTerm, order = sortOrder) => {
    try {
      const response = await teacherServices.getClassroomsByTeacher(null, {
        per_page: 9,
        sort_by: "name",
        sort_dir: order,
        search,
        page,
      });
      setTeacherClassrooms(response?.data);
      setLastPage(response?.last_page);
      setTotalPages(response?.total);
    } catch (error) {
      console.error("Failed to fetch teacher classrooms:", error);
    } finally {
      setLoading(false);
    }
  };

  // Debounce effect
  useEffect(() => {
    const delaySearch = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(delaySearch);
  }, [searchTerm]);

  useEffect(() => {
    setLoading(true); // show loading
    setCurrentPage(1); // Reset to page 1 on new search
  }, [debouncedSearchTerm]);

  useEffect(() => {
    getTeacherClassrooms(currentPage);
  }, [currentPage, debouncedSearchTerm, sortOrder, lang]);

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-start xl:space-x-2">
        {/* Search and Filters Section */}
        <div
          className={`relative mb-2 flex w-full items-center justify-between rounded-xl bg-[#ffffff] dark:bg-[#1F2937] xl:mb-0 xl:w-1/3`}
        >
          <MdSearch className="absolute right-3 top-1/2 -translate-y-1/2 text-xl text-mutedText dark:text-dark-mutedText rtl:left-3 rtl:right-auto" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t("search")}
            className={`w-full rounded-xl border bg-white px-3 py-2 dark:border-dark-surface dark:bg-[#1F2937] dark:placeholder:text-[#BFBFBF]`}
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Dropdown onSelect={(option) => setSortOrder(option === "A - Z" ? "asc" : "desc")}>
            <div className="cursor-pointer rounded-lg px-4 py-2 hover:bg-gray-50 dark:hover:bg-[#FAFBFC0D]">
              A - Z
            </div>
            <div className="cursor-pointer rounded-lg px-4 py-2 hover:bg-gray-50 dark:hover:bg-[#FAFBFC0D]">
              Z - A
            </div>
          </Dropdown>

          <Button
            iconLeft={<MdAddCircleOutline size={22} />}
            label={t("add_new_classroom")}
            color="green"
            onClick={() => openAddClassroomModal(user?.teacher_id)}
          />
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
            {teacherClassrooms.map((classroom) => (
              <div
                key={classroom.id}
                className="relative overflow-hidden rounded-xl bg-white shadow-lg transition dark:bg-[#2C3A4A]"
              >
                <div className="relative p-4">
                  {/* Classroom Header */}
                  <div className="flex items-center justify-between rounded-t-lg border border-b bg-blue-100 p-4 dark:border-[#3A4758] dark:bg-[#3A4758]">
                    <div className="flex items-center gap-4">
                      <img
                        className="h-14 w-14 rounded-lg object-cover"
                        src={classroom?.course?.image || classroom_placeholder}
                        alt={classroom?.name}
                      />
                      <div>
                        <h3
                          className="max-w-[18ch] truncate text-lg font-semibold capitalize text-gray-900 dark:text-white"
                          title={classroom?.name}
                        >
                          {classroom?.name}
                        </h3>
                        <p className="max-w-[18ch] truncate text-sm text-gray-600 dark:text-gray-300">
                          {classroom?.section} - {classroom?.grade?.name}
                        </p>
                      </div>
                    </div>

                    {/* Menu Options */}
                    <div>
                      <Menu
                        menuButton={
                          <MenuButton className="flex items-center justify-center rounded-full bg-blue-200 p-2 hover:bg-blue-50 dark:bg-[#26303d] dark:hover:bg-[#2E3A4A]">
                            <MenuDots className="h-4 w-4 text-blue-500 dark:text-white" />
                          </MenuButton>
                        }
                        transition
                        align="end"
                        menuClassName="rounded-lg p-1 bg-white dark:border-gray-700 dark:bg-[#2C3A4A]"
                      >
                        <MenuItem
                          onClick={() => openDeleteClassroomModal(classroom)}
                          className="flex items-center gap-2 rounded-lg px-2 py-2 text-sm font-medium text-red-600"
                        >
                          <MdOutlineDeleteForever size={20} className="text-red-500" />
                          {t("delete_classroom")}
                        </MenuItem>
                      </Menu>
                    </div>
                  </div>

                  {/* Classroom Info */}
                  <div className="space-y-1 border px-5 py-3 dark:border-[#3A4758]">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-light text-gray-600 dark:text-gray-300">
                        {t("students")}
                      </span>
                      <span className="text-sm font-semibold text-blue-500 dark:text-blue-400">
                        {classroom.student_count}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm font-light text-gray-600 dark:text-gray-300">
                        {t("course")}
                      </span>

                      <TruncatedTextWithTooltip
                        text={classroom?.course?.course}
                        maxLength={20}
                        className="text-sm font-medium text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>

                  {/* View Details Button */}
                  <div className="flex justify-end rounded-b-lg border bg-gray-50 p-4 dark:border-dark-border dark:bg-[#3A4758]">
                    <Button
                      iconRight={<MdChevronRight size={20} className={lang == "ar" && "rotate-180"} />}
                      label={t("view_details")}
                      onClick={() => navigateClassroomDetails(classroom.id)}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      {/* Pagination */}
      {totalPages > 9 && (
        <Pagination lastPage={lastPage} currentPage={currentPage} onPageChange={handlePageChange} />
      )}

      {/* Modals */}
      <AddClassroomModal ref={addClassroomModalRef} onSubmit={handleAddClassroomSuccess} />
      <DeleteClassroomModal ref={deleteClassroomModalRef} onSubmit={handleDeleteClassroomSuccess} />
    </>
  );
};

export default TeacherClassrooms;
